<template>
  <div>
    <b-row class="mb-2">
      <b-col cols="12" class="d-flex justify-content-end">
        <div class="view-toggle" role="group" aria-label="Product view mode">
          <b-button
            variant="link"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'grid' }"
            :pressed="viewMode === 'grid'"
            title="Grid view"
            @click="setViewMode('grid')"
          >
            <feather-icon icon="GridIcon" size="15" />
          </b-button>
          <b-button
            variant="link"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'list' }"
            :pressed="viewMode === 'list'"
            title="List view"
            @click="setViewMode('list')"
          >
            <feather-icon icon="ListIcon" size="15" />
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        v-for="val in productArr"
        :key="val.KODE"
        cols="12"
        :md="viewMode === 'grid' ? 6 : 12"
        :lg="viewMode === 'grid' ? 4 : 12"
        :xl="viewMode === 'grid' ? 3 : 12"
        :class="['d-flex', 'flex-column', viewMode === 'list' ? 'mb-1' : 'mb-2']"
      >
        <ProductBookmarkCard
          class="w-100"
          :item="val"
          :context="productTypeLabel(val)"
          :view-mode="viewMode"
          :show-retired-tag="isRetired(val)"
          :allow-bookmark="!isRetired(val)"
        />
      </b-col>
      <b-col
        v-for="i in 5"
        v-show="loading"
        :key="i"
        cols="12"
        :md="viewMode === 'grid' ? 6 : 12"
        :lg="viewMode === 'grid' ? 4 : 12"
        :xl="viewMode === 'grid' ? 3 : 12"
      >
        <ProductCardSkeleton />
      </b-col>
      <b-col v-if="!productArr.length && !loading" class="text-center">
        <b-card>
          <b-card-text>
            <h3 class="text-muted">No Data, please search another word</h3>
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BButton,
} from 'bootstrap-vue'
import ProductCardSkeleton from '@/views/user/product/components/ProductCardSkeleton.vue'
import ProductBookmarkCard from '@/views/user/product/components/ProductBookmarkCard.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

function normalizeClean(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function normalizeWords(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
}

function levenshteinDistance(a, b) {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length

  const row = Array(a.length + 1)
  for (let j = 0; j <= a.length; j++) row[j] = j

  for (let i = 1; i <= b.length; i++) {
    let prev = i
    for (let j = 1; j <= a.length; j++) {
      const val = b.charAt(i - 1) === a.charAt(j - 1) ? row[j - 1] : Math.min(row[j - 1] + 1, prev + 1, row[j] + 1)
      row[j - 1] = prev
      prev = val
    }
    row[a.length] = prev
  }
  return row[a.length]
}

function charOverlapRatio(queryStr, targetStr) {
  if (!queryStr || !targetStr) return 0
  const qMap = {}
  for (const c of queryStr) {
    qMap[c] = (qMap[c] || 0) + 1
  }
  let matched = 0
  for (const c of targetStr) {
    if (qMap[c] > 0) {
      matched++
      qMap[c]--
    }
  }
  return matched / queryStr.length
}

function getMatchScore(query, item) {
  const label = String(item?.PRODUCT || item?.label || item?.NAME || item?.name || '')
  const kode = String(item?.KODE || item?.kode || item?.CODE || item?.code || '')

  const rawQ = String(query || '').toLowerCase().trim()
  const rawL = label.toLowerCase().trim()
  const rawK = kode.toLowerCase().trim()

  if (!rawQ) return 0

  // 1. Exact raw match
  if (rawL === rawQ || rawK === rawQ) return 100

  // 2. Clean normalized match (ignores hyphens, spaces, dots, special chars)
  const cleanQ = normalizeClean(rawQ)
  const cleanL = normalizeClean(rawL)
  const cleanK = normalizeClean(rawK)

  if (!cleanQ) return 0

  if (cleanL === cleanQ || cleanK === cleanQ) return 95

  // 3. Clean starts with cleanQ (e.g. "sdwan" matches "sdwan direct")
  if (cleanL.startsWith(cleanQ) || cleanK.startsWith(cleanQ)) return 85

  // 4. Clean contains cleanQ (e.g. "sdwan" matches "managed sd-wan")
  if (cleanL.includes(cleanQ) || cleanK.includes(cleanQ)) return 75

  // 5. Token word match (all query words present in target words)
  const qWords = normalizeWords(rawQ)
  const targetWords = [...normalizeWords(rawL), ...normalizeWords(rawK)]
  if (qWords.length > 0) {
    const allTokensFound = qWords.every(qw =>
      targetWords.some(tw => tw.includes(qw) || qw.includes(tw))
    )
    if (allTokensFound) return 65
  }

  // 6. Fuzzy character & Levenshtein similarity matching for typos & random keywords (e.g. "gibla" -> "global", "sdwan" -> "sd-wan")
  if (cleanQ.length >= 3) {
    let maxSimilarity = 0
    let minDistance = Infinity

    // Check against full clean label
    const fullDist = levenshteinDistance(cleanQ, cleanL)
    const fullMaxLen = Math.max(cleanQ.length, cleanL.length)
    if (fullMaxLen > 0) {
      const sim = 1 - fullDist / fullMaxLen
      if (sim > maxSimilarity) maxSimilarity = sim
    }

    // Check against each word token in target label/kode
    for (const tw of targetWords) {
      if (!tw) continue
      const dist = levenshteinDistance(cleanQ, tw)
      if (dist < minDistance) minDistance = dist

      const maxLen = Math.max(cleanQ.length, tw.length)
      if (maxLen > 0) {
        const sim = 1 - dist / maxLen
        if (sim > maxSimilarity) maxSimilarity = sim
      }

      // Also check character overlap ratio
      const overlap = charOverlapRatio(cleanQ, tw)
      if (overlap >= 0.75 && cleanQ[0] === tw[0]) {
        if (maxSimilarity < 0.5) maxSimilarity = 0.5
      }
    }

    if (minDistance <= 3 || maxSimilarity >= 0.4) {
      return Math.round(30 + maxSimilarity * 30)
    }
  }

  return 0
}

function getAltKeywords(keyword) {
  const kw = String(keyword || '').trim().toLowerCase()
  if (!kw) return []

  const variants = new Set()
  const clean = kw.replace(/[^a-z0-9]/g, '')

  // 1. Clean version without hyphens/spaces (e.g. "sd-wan" -> "sdwan", "pc-wan" -> "pcwan")
  if (clean && clean !== kw) {
    variants.add(clean)
  }

  // 2. Version with hyphens/underscores replaced by spaces (e.g. "sd-wan" -> "sd wan")
  if (kw.includes('-') || kw.includes('_')) {
    variants.add(kw.replace(/[-_]+/g, ' '))
  }

  // 3. Dynamic split for single non-hyphenated words (e.g. "sdwan" -> "sd-wan", "pcwan" -> "pc-wan")
  if (!kw.includes('-') && !kw.includes(' ') && kw.length >= 4 && kw.length <= 15) {
    // Dynamic hyphen insertion after 2nd char (common for acronyms like SD-WAN, PC-WAN)
    variants.add(kw.slice(0, 2) + '-' + kw.slice(2))
    // Dynamic space insertion after 2nd char
    variants.add(kw.slice(0, 2) + ' ' + kw.slice(2))
  }

  variants.delete(kw)
  return Array.from(variants).filter(Boolean)
}

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BButton,
    ProductCardSkeleton,
    ProductBookmarkCard,
    FeatherIcon,
  },
  data() {
    return {
      productArr: [],
      loading: true,
      viewMode: 'grid',
    }
  },
  computed: {
    searchWord() {
      let word = ''
      if (typeof this.$route.query.s == 'string') {
        word = this.$route.query.s
      }
      return word
    },
  },
  watch: {
    searchWord: {
      immediate: true,
      handler() {
        this.resetResult()
        this.fetchProduct()
      },
    },
  },
  methods: {
    setViewMode(mode) {
      this.viewMode = mode === 'list' ? 'list' : 'grid'
    },
    resetResult() {
      this.productArr = []
    },
    productTypeLabel(item = {}) {
      const raw =
        item.PROD_CATEGORY ??
        item.prod_category ??
        item.product_category_details?.name ??
        ''
      const numeric = Number(raw)
      if (numeric === 1) return 'Communication'
      if (numeric === 2) return 'Datacomm'
      if (numeric === 3) return 'Internet'
      if (numeric === 4) return 'Wireless'
      if (numeric === 5) return 'Cyber Security'
      const normalized = String(raw)
        .trim()
        .toLowerCase()
      if (normalized.includes('communication')) return 'Communication'
      if (normalized.includes('datacomm')) return 'Datacomm'
      if (normalized.includes('internet')) return 'Internet'
      if (normalized.includes('wireless')) return 'Wireless'
      if (normalized.includes('cyber')) return 'Cyber Security'
      return 'No Category'
    },
    isRetired(item = {}) {
      return Number(item.RETIREMENT ?? item.retirement) === 1
    },
    fetchProduct() {
      const keyword = String(this.searchWord || '').trim()
      if (!keyword) {
        this.loading = false
        return
      }

      const normalizeRecords = response =>
        response?.data?.data?.records ||
        response?.data?.records ||
        response?.data?.data ||
        []

      const applyRecords = rows => {
        const records = Array.isArray(rows) ? rows : []
        const sanitized = this.sanitizeHighlight(records).filter(this.isPublished)
        this.productArr = sanitized
        this.persistSuggestionProducts(sanitized)
      }

      this.loading = true

      // Unified search across ES, Browse API variants, and Full Product Catalog
      const mapRequest = {
        map: {
          KODE: 's',
          PRODUCT: 's',
          SHORT_DESCRIPTION: 'd',
          PROD_CATEGORY: 'd',
          LAST_UPDATE: 'd',
          RETIREMENT: 'd',
          PROD_SHOW_STATUS: 'd',
          photo_product: 'd',
          child_details: 'd',
          product_category_details: 'd',
          is_bookmarked: 'd',
        },
      }

      const altKeywords = getAltKeywords(keyword)

      const promises = [
        // 1. ES Search with original keyword
        this.$http.get(`/product/es/take/100/skip/0/es/${encodeURIComponent(keyword)}`, { timeout: 8000 }).catch(() => null),
        // 2. Browse API with original keyword & full mapRequest (published only)
        this.$http.post(`/product/browse/take/100/skip/0/orderBy.PRODUCT/asc/s/${encodeURIComponent(keyword)}/published/true`, mapRequest, { skipGlobalErrorNotif: true }).catch(() => null),
      ]

      altKeywords.forEach(alt => {
        promises.push(
          this.$http.get(`/product/es/take/100/skip/0/es/${encodeURIComponent(alt)}`, { timeout: 8000 }).catch(() => null),
          this.$http.post(`/product/browse/take/100/skip/0/orderBy.PRODUCT/asc/s/${encodeURIComponent(alt)}/published/true`, mapRequest, { skipGlobalErrorNotif: true }).catch(() => null)
        )
      })

      Promise.all(promises)
        .then(responses => {
          const rawCombined = []
          responses.forEach(res => {
            if (res) {
              const recs = normalizeRecords(res)
              if (Array.isArray(recs)) rawCombined.push(...recs)
            }
          })

          // Deduplicate by product KODE or PRODUCT name and merge complete fields
          const mapByKode = new Map()
          rawCombined.forEach(item => {
            const k = String(item.KODE || item.kode || item.PRODUCT || item.label || '').trim()
            if (!k) return
            const existing = mapByKode.get(k)
            if (!existing) {
              mapByKode.set(k, { ...item })
            } else {
              const merged = { ...existing }
              Object.keys(item).forEach(key => {
                if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
                  if (merged[key] === undefined || merged[key] === null || merged[key] === '') {
                    merged[key] = item[key]
                  }
                }
              })
              mapByKode.set(k, merged)
            }
          })

          const allItems = Array.from(mapByKode.values())

          // Score each item with fuzzy and normalized similarity algorithm
          const scored = allItems
            .map(item => ({
              item,
              score: getMatchScore(keyword, item)
            }))
            .filter(entry => entry.score > 0)
            .sort((a, b) => b.score - a.score)
            .map(entry => entry.item)

          applyRecords(scored)
        })
        .catch(err => {
          console.error('Unified search error:', err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    isPublished(item = {}) {
      // Exclude retired products
      if (Number(item.RETIREMENT ?? item.retirement) === 1) return false

      // Exclude archived/unpublished status strings
      const statusStr = String(item.STATUS ?? item.status ?? '').toLowerCase()
      if (statusStr.includes('archive') || statusStr.includes('unpublish')) return false

      // Exclude when PROD_SHOW_STATUS is explicitly 0 or false
      const status = item.PROD_SHOW_STATUS ?? item.prod_show_status
      if (status !== undefined && status !== null) {
        if (Number(status) === 0 || String(status) === '0' || status === false) return false
      }
      return true
    },
    sanitizeHighlight(data) {
      if (!Array.isArray(data)) return []
      return data.map(item => {
        if (!!item.highlight) {
          const [highlight] = Object.entries(item.highlight)
          const highlightedName = (highlight ?? [])[1]?.[0]
          if (highlightedName) {
            item.PRODUCT = this.stripHtml(highlightedName)
          }
        }
        return item
      })
    },
    persistSuggestionProducts(records = []) {
      if (!Array.isArray(records) || !records.length) return
      try {
        const key = 'product_search_products'
        const raw = localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : []
        const map = new Map()

        if (Array.isArray(parsed)) {
          parsed.forEach(item => {
            const label = String(item?.label || '').trim()
            if (!label) return
            map.set(label.toLowerCase(), {
              label,
              kode: item?.kode || '',
            })
          })
        }

        records.forEach(item => {
          const label = String(item?.PRODUCT || '').trim()
          if (!label) return
          map.set(label.toLowerCase(), {
            label,
            kode: item?.KODE || '',
          })
        })

        localStorage.setItem(
          key,
          JSON.stringify(Array.from(map.values()).slice(0, 300))
        )
      } catch (e) {
        // ignore localStorage errors
      }
    },
    stripHtml(html = '') {
      const tmp = document.createElement('div')
      tmp.innerHTML = html
      return (tmp.textContent || tmp.innerText || '').trim()
    },
  },
}
</script>

<style scoped>
.view-toggle {
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
}

.view-toggle-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 999px;
  color: #64748b !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none !important;
}

.view-toggle-btn:hover {
  background: rgba(148, 163, 184, 0.18);
  color: #334155 !important;
}

.view-toggle-btn.active {
  background: #ef4444;
  color: #ffffff !important;
  box-shadow: 0 8px 18px rgba(239, 68, 68, 0.35);
}
</style>
